import { IPatient } from '../patient/patient.types';
import {
    IClinician,
    IMedicalRequest,
    IMedicalRequestContent,
    IMedicalRequestContentItem,
    IRequestStatusDates,
    RequestStatus
} from './request.types';
import { requestTypeLabel } from '../../request-type.labels';

/**
 * Medical Request model class - UPDATED to work with API strings
 * Provides computed properties for UI display and request operations
 */
export class MedicalRequest implements Required<IMedicalRequest> {
    // Core properties from API - matching OpenAPI spec exactly
    entityId: string;
    zTouchTimestamp: string | null;
    zCreationTimestamp: string | null;
    status: RequestStatus;
    speciality: string;                      // Now string from API
    requestType: string;                     // Now string from API
    content: MedicalRequestContent;
    isRead: boolean;
    clinicianId: string;
    patientId: string;
    handleBy: string;
    clinician: IClinician | null;
    patient: IPatient | null;
    expertAssigned: IClinician | null;
    requestStatusDates: IRequestStatusDates | null;
    ztouchTimestamp: string;
    zcreationTimestamp: string;

    /**
     * Returns request title for display
     */
    get title(): string {
        const patientName = this.patientFullName;
        const typeDisplay = requestTypeLabel(this.requestType);
        return `${typeDisplay} - ${patientName}`;
    }

    /**
     * Returns request display name with patient info
     */
    get displayName(): string {
        return `${requestTypeLabel(this.requestType)} for ${this.patientFullName}`;
    }

    /**
     * Returns request type display name - simplified
     */
    get requestTypeDisplay(): string {
        return requestTypeLabel(this.requestType);
    }

    /**
     * Returns speciality display name - simplified
     */
    get specialityDisplay(): string {
        return this.speciality; // Use API string directly
    }

    /**
     * Returns status display name
     */
    get statusDisplay(): string {
        switch (this.status) {
            case 'DRAFT':
                return 'Draft';
            case 'WAITING':
                return 'Waiting';
            case 'IN_PROGRESS':
                return 'In Progress';
            case 'COMPLETED':
                return 'Completed';
            case 'ARCHIVED':
                return 'Archived';
            default:
                return this.status;
        }
    }

    /**
     * Returns status color class for UI
     */
    get statusColor(): string {
        switch (this.status) {
            case 'DRAFT':
                return 'text-gray-600 bg-gray-100';
            case 'WAITING':
                return 'text-yellow-600 bg-yellow-100';
            case 'IN_PROGRESS':
                return 'text-blue-600 bg-blue-100';
            case 'COMPLETED':
                return 'text-green-600 bg-green-100';
            case 'ARCHIVED':
                return 'text-gray-500 bg-gray-50';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    }

    /**
     * Returns request type icon - simplified mapping
     */
    get requestTypeIcon(): string {
        const type = this.requestType.toLowerCase();
        switch (type) {
            case 'ecg':
                return 'heroicons_outline:heart';
            case 'holter':
                return 'heroicons_outline:device-phone-mobile';
            case 'dermatology_photos':
                return 'heroicons_outline:camera';
            case 'mri brain':
            case 'mri_brain':
                return 'heroicons_outline:cpu-chip';
            case 'eeg':
                return 'heroicons_outline:bolt';
            case 'skin biopsy':
            case 'skin_biopsy':
                return 'heroicons_outline:scissors';
            case 'general checkup':
            case 'general_checkup':
                return 'heroicons_outline:clipboard-document-check';
            default:
                return 'heroicons_outline:document';
        }
    }

    /**
     * Returns speciality icon - simplified mapping
     */
    get specialityIcon(): string {
        const speciality = this.speciality.toLowerCase();
        switch (speciality) {
            case 'cardiology':
                return 'heroicons_outline:heart';
            case 'neurology':
                return 'heroicons_outline:cpu-chip';
            case 'dermatology':
                return 'heroicons_outline:camera';
            case 'general medicine':
            case 'general_medicine':
                return 'heroicons_outline:user';
            default:
                return 'heroicons_outline:document';
        }
    }

    /**
     * Returns patient full name - handles case where patient data might not be populated
     */
    get patientFullName(): string {
        if (!this.patient) {
            return `Patient ${this.patientId.substring(0, 8)}...`;
        }
        
        const firstName = this.patient.firstName || '';
        const lastName = this.patient.lastName || '';
        return firstName ? `${firstName} ${lastName}` : lastName;
    }

    /**
     * Returns clinician full name - handles case where clinician data might not be populated
     */
    get clinicianFullName(): string {
        if (!this.clinician) {
            return `Clinician ${this.clinicianId.substring(0, 8)}...`;
        }
        
        const firstName = this.clinician.firstName || '';
        const lastName = this.clinician.lastName || '';
        return firstName ? `${firstName} ${lastName}` : lastName;
    }

    /**
     * Returns patient age if available
     */
    get patientAge(): number | null {
        if (!this.patient?.birthDate) return null;

        const today = new Date();
        const birthDate = new Date(this.patient.birthDate);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return Math.max(0, age);
    }

    /**
     * Returns formatted patient age with proper units (days, weeks, months, years)
     * If patient.birthDate is available, uses intelligent formatting (days/weeks/months/years)
     * Otherwise falls back to patientAge property (years only)
     */
    get patientAgeFormatted(): string {
        // Try to use patient.birthDate for intelligent formatting
        if (this.patient?.birthDate) {
            const birthDate = new Date(this.patient.birthDate);
            const today = new Date();
            const diffTime = today.getTime() - birthDate.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            // Less than 7 days: show days
            if (diffDays < 7) {
                const ageText = diffDays === 0 ? 'Nouveau-né' : diffDays === 1 ? '1 jour' : `${diffDays} jours`;
                return `Âge : ${ageText}`;
            }

            // Less than 8 weeks (56 days): show weeks
            if (diffDays < 56) {
                const weeks = Math.floor(diffDays / 7);
                const ageText = weeks === 1 ? '1 semaine' : `${weeks} semaines`;
                return `Âge : ${ageText}`;
            }

            // Calculate months
            let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
            months += today.getMonth() - birthDate.getMonth();
            if (today.getDate() < birthDate.getDate()) {
                months--;
            }
            months = Math.max(0, months);

            // Less than 24 months: show months
            if (months < 24) {
                const ageText = months === 1 ? '1 mois' : `${months} mois`;
                return `Âge : ${ageText}`;
            }

            // 24 months and above: show years
            const age = this.patientAge;
            if (age !== null) {
                const ageText = age === 1 ? '1 an' : `${age} ans`;
                return `Âge : ${ageText}`;
            }
        }

        // Fallback: check if we have a numeric patientAge property (from API)
        // This happens when patient object is not fully loaded but age is available
        if (this.patientAge !== null && this.patientAge !== undefined) {
            const ageText = this.patientAge === 1 ? '1 an' : `${this.patientAge} ans`;
            return `Âge : ${ageText}`;
        }

        return 'Âge inconnu';
    }

    /**
     * Returns formatted creation date
     */
    get createdAtFormatted(): string {
        if (!this.zcreationTimestamp) return 'Unknown';
        
        try {
            const date = new Date(this.zcreationTimestamp);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns formatted modification date
     */
    get modifiedAtFormatted(): string {
        if (!this.ztouchTimestamp) return 'Unknown';
        
        try {
            const date = new Date(this.ztouchTimestamp);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns the primary ID (entityId for API compatibility)
     */
    get id(): string {
        return this.entityId;
    }

    /**
     * Returns request summary/description from content
     */
    get summary(): string {
        const messageItem = this.content.items.find(item => item.name === 'message');
        return messageItem?.value || 'No description available';
    }

    /**
     * Returns whether request can be edited
     */
    get canEdit(): boolean {
        return this.status === 'DRAFT';
    }

    /**
     * Returns whether request can be sent
     */
    get canSend(): boolean {
        return this.status === 'DRAFT';
    }

    /**
     * Returns whether request can be archived
     */
    get canArchive(): boolean {
        return this.status === 'COMPLETED' || this.status === 'IN_PROGRESS' || this.status === 'WAITING';
    }

    /**
     * Returns text content items
     */
    get textItems(): MedicalRequestContentItem[] {
        return this.content.items.filter(item => item.type === 'TEXT');
    }

    /**
     * Returns file content items
     */
    get fileItems(): MedicalRequestContentItem[] {
        return this.content.items.filter(item => item.type === 'FILE');
    }

    /**
     * Constructor - initializes MedicalRequest from API response
     */
    constructor(request: IMedicalRequest) {
        // Required fields
        this.entityId = request.entityId;
        this.status = request.status;
        this.speciality = request.speciality;         // Now string from API
        this.requestType = request.requestType;       // Now string from API
        this.content = new MedicalRequestContent(request.content);
        this.clinicianId = request.clinicianId;
        this.patientId = request.patientId;
        this.ztouchTimestamp = request.ztouchTimestamp;
        this.zcreationTimestamp = request.zcreationTimestamp;
        
        // Optional fields with defaults
        this.zTouchTimestamp = request.zTouchTimestamp || null;
        this.zCreationTimestamp = request.zCreationTimestamp || null;
        this.isRead = request.isRead || false;
        this.handleBy = request.handleBy;
        this.clinician = request.clinician || null;
        this.patient = request.patient || null;
        this.expertAssigned = request.expertAssigned || null;
        this.requestStatusDates = request.requestStatusDates || null;
    }

    /**
     * Returns timeline events based on request status dates
     */
    getTimelineEvents(): Array<{status: RequestStatus | 'CREATION' | 'MODIFICATION', date: string, label: string}> {
        const events: Array<{status: RequestStatus | 'CREATION' | 'MODIFICATION', date: string, label: string}> = [];

        // Always add creation event
        events.push({
            status: 'CREATION',
            date: this.zcreationTimestamp,
            label: 'Demande créée'
        });

        // Add status transition events if available
        if (this.requestStatusDates) {
            const statusLabels: Record<RequestStatus, string> = {
                'DRAFT': 'Brouillon créé',
                'WAITING': 'Envoyée aux experts',
                'IN_PROGRESS': 'Prise en charge par un expert',
                'COMPLETED': 'Avis médical fourni',
                'ARCHIVED': 'Demande archivée'
            };

            // Add events for all non-null status dates
            Object.entries(this.requestStatusDates).forEach(([status, date]) => {
                if (date) {
                    events.push({
                        status: status as RequestStatus,
                        date: date,
                        label: statusLabels[status as RequestStatus]
                    });
                }
            });
        }

        // Add modification event only if it's meaningfully different from creation and not already covered
        if (this.ztouchTimestamp !== this.zcreationTimestamp) {
            // Check if we already have an event at this exact timestamp
            const existingEvent = events.find(event => event.date === this.ztouchTimestamp);

            // Calculate time difference to avoid showing modifications that are essentially the same moment
            const creationTime = new Date(this.zcreationTimestamp).getTime();
            const touchTime = new Date(this.ztouchTimestamp).getTime();
            const timeDifferenceSeconds = Math.abs(touchTime - creationTime) / 1000;

            // Only add modification if:
            // 1. Not already covered by another event
            // 2. Time difference is more than 5 seconds (meaningful modification)
            if (!existingEvent && timeDifferenceSeconds > 5) {
                events.push({
                    status: 'MODIFICATION',
                    date: this.ztouchTimestamp,
                    label: 'Dernière modification'
                });
            }
        }

        // Sort events by date
        return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    /**
     * Converts request model to DTO for API requests
     */
    toDTO(): IMedicalRequest {
        return {
            entityId: this.entityId,
            zTouchTimestamp: this.zTouchTimestamp,
            zCreationTimestamp: this.zCreationTimestamp,
            status: this.status,
            speciality: this.speciality,
            requestType: this.requestType,
            content: this.content.toDTO(),
            isRead: this.isRead,
            clinicianId: this.clinicianId,
            patientId: this.patientId,
            handleBy: this.handleBy,
            clinician: this.clinician,
            patient: this.patient,
            expertAssigned: this.expertAssigned,
            requestStatusDates: this.requestStatusDates,
            ztouchTimestamp: this.ztouchTimestamp,
            zcreationTimestamp: this.zcreationTimestamp
        };
    }

    /**
     * Checks if request matches search term
     */
    matchesSearch(searchTerm: string): boolean {
        if (!searchTerm) return true;
        
        const term = searchTerm.toLowerCase();
        const searchableFields = [
            this.patientFullName.toLowerCase(),
            this.clinicianFullName.toLowerCase(),
            this.summary.toLowerCase(),
            this.requestType.toLowerCase(),
            this.speciality.toLowerCase(),
            this.statusDisplay.toLowerCase(),
        ];
        
        return searchableFields.some(field => field.includes(term));
    }

    /**
     * Clone request instance
     */
    clone(): MedicalRequest {
        return new MedicalRequest(this.toDTO());
    }
}

/**
 * Medical Request Content model class
 */
export class MedicalRequestContent implements IMedicalRequestContent {
    items: MedicalRequestContentItem[];

    constructor(content: IMedicalRequestContent) {
        this.items = content.items.map(item => new MedicalRequestContentItem(item));
    }

    /**
     * Gets content item by name
     */
    getItem(name: string): MedicalRequestContentItem | undefined {
        return this.items.find(item => item.name === name);
    }

    /**
     * Gets content item value by name
     */
    getValue(name: string): string | undefined {
        return this.getItem(name)?.value;
    }

    /**
     * Sets content item value
     */
    setValue(name: string, value: string, type: 'TEXT' | 'FILE' = 'TEXT'): void {
        const existingItem = this.getItem(name);
        if (existingItem) {
            existingItem.value = value;
            existingItem.type = type;
        } else {
            this.items.push(new MedicalRequestContentItem({ name, type, value }));
        }
    }

    /**
     * Removes content item by name
     */
    removeItem(name: string): void {
        this.items = this.items.filter(item => item.name !== name);
    }

    /**
     * Gets all text items
     */
    getTextItems(): MedicalRequestContentItem[] {
        return this.items.filter(item => item.type === 'TEXT');
    }

    /**
     * Gets all file items
     */
    getFileItems(): MedicalRequestContentItem[] {
        return this.items.filter(item => item.type === 'FILE');
    }

    /**
     * Converts to DTO
     */
    toDTO(): IMedicalRequestContent {
        return {
            items: this.items.map(item => item.toDTO())
        };
    }
}

/**
 * Medical Request Content Item model class
 */
export class MedicalRequestContentItem implements IMedicalRequestContentItem {
    name: string;
    type: 'TEXT' | 'FILE';
    value: string;

    constructor(item: IMedicalRequestContentItem) {
        this.name = item.name;
        this.type = item.type;
        this.value = item.value;
    }

    /**
     * Returns display label for the field
     */
    get displayLabel(): string {
        // Convert camelCase/snake_case to Title Case
        return this.name
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    /**
     * Returns whether this is a file item
     */
    get isFile(): boolean {
        return this.type === 'FILE';
    }

    /**
     * Returns whether this is a text item
     */
    get isText(): boolean {
        return this.type === 'TEXT';
    }

    /**
     * Returns formatted value for display
     */
    get displayValue(): string {
        if (this.isFile && this.value) {
            // For files, value might be a file ID or file path
            return this.value.split('/').pop() || this.value;
        }
        return this.value;
    }

    /**
     * Converts to DTO
     */
    toDTO(): IMedicalRequestContentItem {
        return {
            name: this.name,
            type: this.type,
            value: this.value
        };
    }
}